import { BOUNTY_CONFIG } from '@/config/constants';

export type ProseTag =
  | 'p' | 'div' | 'span' | 'h1' | 'h2' | 'h3' | 'strong' | 'em' | 'u' | 's'
  | 'ul' | 'ol' | 'li' | 'blockquote' | 'pre' | 'code' | 'br' | 'hr' | 'a';

export type ProseNode = string | {
  tag: ProseTag;
  children: ProseNode[];
  href?: string;
  start?: number;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
};

export interface ParsedProse {
  nodes: ProseNode[];
  text: string;
  hasUnsafeMarkup: boolean;
  invalid?: string;
}

const tags: Record<string, ProseTag> = {
  p: 'p', div: 'div', span: 'span', h1: 'h1', h2: 'h2', h3: 'h3',
  strong: 'strong', b: 'strong', em: 'em', i: 'em', u: 'u',
  s: 's', strike: 's', del: 's', ul: 'ul', ol: 'ol', li: 'li',
  blockquote: 'blockquote', pre: 'pre', code: 'code', br: 'br', hr: 'hr', a: 'a',
};
const droppedTags = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'applet', 'svg', 'math',
  'template', 'noscript', 'form', 'input', 'button', 'textarea', 'select',
  'option', 'img', 'picture', 'video', 'audio', 'source', 'track', 'canvas',
  'link', 'meta', 'base', 'head',
]);
const blockTags = new Set(['p', 'div', 'h1', 'h2', 'h3', 'li', 'blockquote', 'pre', 'ul', 'ol']);
const alignments = new Set(['left', 'center', 'right', 'justify']);
const markupPattern = /<\/?(?:p|div|span|h[1-6]|strong|b|em|i|u|s|strike|del|ul|ol|li|blockquote|pre|code|br|hr|a|script|style|iframe|object|embed|applet|svg|math|template|noscript|form|input|button|textarea|select|img|picture|video|audio|source|track|canvas|link|meta|base|head|table|html|body)\b[^>]*>/i;

/** Normalize transport line endings without flattening paragraphs or Unicode. */
export const normalizeProse = (input: string): string => input.replace(/\r\n?/g, '\n');

/** Only explicit HTTP(S) links become interactive; never infer a protocol. */
export function safeProseHref(input: string): string | undefined {
  const href = input.trim();
  if (/[\s\\]/u.test(href) || Array.from(href).some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127) || !/^https?:\/\//i.test(href)) return undefined;
  try {
    const url = new URL(href);
    if (!url.hostname || url.username || url.password) return undefined;
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function nodesToText(nodes: ProseNode[]): string {
  return nodes.map(node => {
    if (typeof node === 'string') return node;
    if (node.tag === 'br' || node.tag === 'hr') return '\n';
    const text = nodesToText(node.children);
    return blockTags.has(node.tag) ? `${text}\n` : text;
  }).join('');
}

/**
 * Parse legacy editor HTML in an inert template, never in the live document.
 * Only this small data tree leaves the parser; DOM nodes and raw attributes do not.
 * The renderer must create React elements from the tree, not serialize it as HTML.
 */
export function parseProse(input: string): ParsedProse {
  const source = normalizeProse(input);
  const fallback = (invalid?: string): ParsedProse => ({
    nodes: [source], text: source, hasUnsafeMarkup: false, invalid,
  });
  if (!markupPattern.test(source)) return fallback();
  if (source.length > 50000) return fallback('Rich text is too large');
  if (typeof document === 'undefined') return fallback('Rich text parsing is unavailable');

  const template = document.createElement('template');
  // Template contents are inert: no scripts, resource loading, or custom elements run.
  template.innerHTML = source;
  let hasUnsafeMarkup = false;
  let count = 0;
  let invalid: string | undefined;

  const visit = (parent: ParentNode, depth: number): ProseNode[] => {
    if (depth > 50) {
      invalid = 'Rich text is nested too deeply';
      return [];
    }
    const result: ProseNode[] = [];
    for (const child of Array.from(parent.childNodes)) {
      if (++count > 4000) {
        invalid = 'Rich text has too many elements';
        break;
      }
      if (child.nodeType === 3) {
        result.push(child.textContent || '');
        continue;
      }
      if (child.nodeType !== 1) continue;
      const element = child as HTMLElement;
      const name = element.localName.toLowerCase();
      if (element.namespaceURI !== 'http://www.w3.org/1999/xhtml' || droppedTags.has(name)) {
        hasUnsafeMarkup = true;
        continue;
      }
      const tag = Object.prototype.hasOwnProperty.call(tags, name) ? tags[name] : undefined;
      if (!tag) {
        hasUnsafeMarkup = true;
        result.push(...visit(element, depth + 1));
        continue;
      }
      const node: Exclude<ProseNode, string> = { tag, children: visit(element, depth + 1) };
      for (const attribute of Array.from(element.attributes)) {
        if (/^on/i.test(attribute.name) || ['src', 'srcdoc', 'action', 'formaction', 'xlink:href'].includes(attribute.name)) {
          hasUnsafeMarkup = true;
        }
      }
      if (tag === 'a' && element.hasAttribute('href')) {
        node.href = safeProseHref(element.getAttribute('href') || '');
        if (!node.href) hasUnsafeMarkup = true;
      }
      if (tag === 'ol' && element.hasAttribute('start')) {
        const start = Number(element.getAttribute('start'));
        if (Number.isSafeInteger(start) && start >= 1 && start <= 1000000) node.start = start;
      }
      if (['p', 'h1', 'h2', 'h3'].includes(tag)) {
        const alignment = element.style.textAlign;
        if (alignments.has(alignment)) node.textAlign = alignment as typeof node.textAlign;
      }
      result.push(node);
    }
    return result;
  };

  const nodes = visit(template.content, 0);
  if (invalid) return { ...fallback(invalid), hasUnsafeMarkup };
  return { nodes, text: nodesToText(nodes).trim(), hasUnsafeMarkup };
}

export const BOUNTY_PROSE_RULES = {
  title: { min: BOUNTY_CONFIG.MIN_TITLE_LENGTH, max: BOUNTY_CONFIG.MAX_TITLE_LENGTH, rich: false },
  description: { min: BOUNTY_CONFIG.MIN_DESCRIPTION_LENGTH, max: BOUNTY_CONFIG.MAX_DESCRIPTION_LENGTH, rich: true },
  instructions: { min: 0, max: 1000, rich: false },
  location: { min: 0, max: BOUNTY_CONFIG.MAX_LOCATION_LENGTH, rich: false },
} as const;

export type BountyProseField = keyof typeof BOUNTY_PROSE_RULES;
export type ProseValidationResult =
  | { allowed: true; sanitizedInput: string }
  | { allowed: false; reason: string; threatDetected?: boolean };

/**
 * Explicitly scoped to display-only bounty prose, never paths, URLs, or accounts.
 * SQL/shell vocabulary is data here, not executable syntax. Persist via bound RPC
 * parameters and render rich descriptions only through SafeRichText. No telemetry.
 */
export function validateProse(input: unknown, field: BountyProseField): ProseValidationResult {
  if (!Object.prototype.hasOwnProperty.call(BOUNTY_PROSE_RULES, field)) {
    return { allowed: false, reason: 'Unknown prose field' };
  }
  if (typeof input !== 'string') return { allowed: false, reason: `${field} must be text` };
  const rule = BOUNTY_PROSE_RULES[field];
  const source = normalizeProse(input).trim();
  const hasControl = Array.from(source).some(char => {
    const code = char.charCodeAt(0);
    return (code < 32 && code !== 9 && code !== 10) || code === 127;
  });
  if (hasControl) return { allowed: false, reason: `${field} contains unsupported control characters` };
  if (field === 'title' && source.includes('\n')) return { allowed: false, reason: 'Title must be a single line' };
  if (source.length > (rule.rich ? 50000 : rule.max)) {
    return { allowed: false, reason: `${field} is too long` };
  }
  const parsed = parseProse(source);
  if (parsed.invalid) return { allowed: false, reason: parsed.invalid };
  if (parsed.hasUnsafeMarkup) {
    return { allowed: false, reason: `${field} contains unsupported or unsafe markup`, threatDetected: true };
  }
  const length = (rule.rich ? parsed.text : source).length;
  if (length < rule.min || length > rule.max) {
    return { allowed: false, reason: `${field} must be between ${rule.min} and ${rule.max} characters` };
  }
  return { allowed: true, sanitizedInput: source };
}
